/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { HtmlHeadInclude, IAny, IGeneric } from '@msdyn365-commerce/core';
import * as React from 'react';
import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';
import { IExtendedKrannichCategoryPageSummaryData } from './krannich-category-page-summary.data';
import { IKrannichCategoryPageSummaryProps } from './krannich-category-page-summary.props.autogenerated';
/**
 *
 * DefaultPageSummaryMetadata component.
 * @extends {React.PureComponent<DefaultPageSummaryProps>}
 */
class KrannichCategoryPageSummary extends React.PureComponent<IKrannichCategoryPageSummaryProps<IExtendedKrannichCategoryPageSummaryData>> {
    public render(): JSX.Element {
        const { data } = this.props;
        var categories: string[] = [];
        if (this.props.data.pageSummary.result === undefined) {
            throw new Error('PageSummaryData input to page-summary is undefined. Unable to generate page-summary');
        }
        let {
            data: {
                pageSummary: {
                    result: { title, description, keywords, sharingImageUrl, faviconUrl }
                }
            }
        } = this.props;

        const {
            config,
            context,
            data: {
                pageSummary: {
                    result: { canonicalUrl }
                }
            }
        } = this.props;

        // Override values coming from data action if config values are provided
        title = config.title || title;
        description = config.description || description;
        keywords = config.keywords || keywords;
        sharingImageUrl = (config.sharingImage && config.sharingImage.src) || sharingImageUrl;
        faviconUrl = config.faviconUrl || faviconUrl;
        const categoryHierarchy = data.categoryHierarchy.result;
        if (!categoryHierarchy) {
            this.props.context.telemetry.error('Category Hierarchy content is empty, module wont render');
        } else {
            categoryHierarchy.map((value: CategoryHierarchy, index: number) => {
                if (value.Name?.toLowerCase() !== 'products' && value.Name !== title) {
                    categories.push(value.Name!);
                }
            });
        }
        const pageTitle = this._constructPageTitle(title, context && context.request && context.request.app);
        let updatePageTitle: string | undefined = '';
        const categoriesTitle = this._constructCategoriesTitle(categories);
        if (categories.length > 0) {
            updatePageTitle = pageTitle?.concat(' | ', categoriesTitle!);
        } else {
            updatePageTitle = pageTitle?.concat(categoriesTitle!);
        }
        return (
            <>
                <HtmlHeadInclude>
                    {pageTitle && <title>{updatePageTitle}</title>}
                    {description && <meta name='description' content={description} />}
                    {keywords && <meta name='keywords' content={keywords} />}
                    {canonicalUrl && <link rel='canonical' href={canonicalUrl} />}
                    {faviconUrl && <link rel='shortcut icon' href={faviconUrl} />}
                </HtmlHeadInclude>
                {!config.disableOgTags && this._renderOgTags(pageTitle, description, sharingImageUrl)}
                {!config.disableTwitterTags && this._renderTwitterTags(pageTitle, description, sharingImageUrl)}
            </>
        );
    }
    private _constructCategoriesTitle(categories: string[] | undefined) {
        var drawTitle =
            categories &&
            categories
                .reverse()
                .join(' | ')
                .concat(' | ', 'Krannich Solar');
        return drawTitle;
    }
    private _renderOgTags(title: string | undefined, description: string | undefined, sharingImageUrl: string | undefined): JSX.Element {
        return (
            <HtmlHeadInclude>
                {title && <meta property='og:title' content={title} />}
                {description && <meta property='og:description' content={description} />}
                {sharingImageUrl && <meta property='og:image' content={sharingImageUrl} />}
                <meta property='og:type' content='website' />
            </HtmlHeadInclude>
        );
    }

    private _renderTwitterTags(
        title: string | undefined,
        description: string | undefined,
        sharingImageUrl: string | undefined
    ): JSX.Element {
        return (
            <HtmlHeadInclude>
                {title && <meta name='twitter:title' content={title} />}
                {description && <meta name='twitter:description' content={description} />}
                {sharingImageUrl && <meta name='twitter:image' content={sharingImageUrl} />}
                <meta name='twitter:card' content='summary' />
            </HtmlHeadInclude>
        );
    }

    private _constructPageTitle(title: string | undefined, app: IGeneric<IAny>): string | undefined {
        if (!title) {
            title = '';
        }

        const moduleConfig = this.props.config;
        if (moduleConfig && moduleConfig.ignoreTitleSuffixPrefix) {
            return title;
        }

        const appConfig = app && app.platform;
        if (appConfig) {
            if (appConfig.pageTitlePrefix) {
                title = (appConfig.pageTitlePrefix as string) + title;
            }
            if (appConfig.pageTitleSuffix) {
                title += appConfig.pageTitleSuffix as string;
            }
        }

        return title;
    }
}

export default KrannichCategoryPageSummary;
